import { useEffect, useRef, useMemo } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import type { CartItem } from '../../services/cartService';
import type { ShoppingCartData } from './shoppingCartTypes';

/**
 * Temporarily locks dates for all accommodation items in the cart (20-minute hold).
 *
 * Behaviour:
 * - When dates are set and accommodation items are in the cart → lock each accommodation.
 * - When dates change → the Convex mutation auto-replaces the old lock for each accommodation.
 * - When the accommodation list changes (item removed) → release all session locks then
 *   re-lock remaining accommodations with the current dates.
 * - When dates are completely cleared → release all session locks.
 * - On unmount (page close / navigation) → release all session locks.
 */
export function useCartDateLock(params: {
  shoppingCartData: ShoppingCartData | null | undefined;
  cartItems: CartItem[];
}) {
  const { shoppingCartData, cartItems } = params;

  const sessionIdRef = useRef<string>(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
  );

  const createLock = useMutation(api.temporaryReservations.create);
  const releaseSession = useMutation(api.temporaryReservations.releaseSession);

  const checkIn = shoppingCartData?.startDate
    ? shoppingCartData.startDate.toISOString().split('T')[0]
    : '';
  const checkOut = shoppingCartData?.endDate
    ? shoppingCartData.endDate.toISOString().split('T')[0]
    : '';

  const accommodationIds = useMemo(
    () => cartItems.filter((i) => i.type === 'accommodation').map((i) => i.itemId),
    [cartItems],
  );

  // Stable string key for the effect dependency (avoids array reference churn)
  const accommodationIdsKey = accommodationIds.join(',');

  // When dates or accommodation list changes: update locks
  useEffect(() => {
    const sessionId = sessionIdRef.current;

    if (!checkIn || !checkOut || accommodationIds.length === 0) {
      // No dates selected or no accommodation in cart → release everything
      releaseSession({ sessionId }).catch(() => {});
      return;
    }

    // When the accommodation list changes we first release the whole session
    // (to drop locks for items that were removed), then re-lock the current ones.
    // For pure date changes the Convex mutation replaces the old lock automatically.
    const lock = async () => {
      // Release first only when the accommodation list changed (key changed between renders)
      // so we don't leave orphaned locks for removed items.
      await releaseSession({ sessionId }).catch(() => {});

      await Promise.all(
        accommodationIds.map((id) =>
          createLock({
            accommodationId: id as Id<'accommodations'>,
            checkIn,
            checkOut,
            sessionId,
          }).catch(() => {}),
        ),
      );
    };

    lock();
     
  }, [checkIn, checkOut, accommodationIdsKey]);

  // Release all locks when the context/portal unmounts (session ends)
  useEffect(() => {
    const sessionId = sessionIdRef.current;
    return () => {
      releaseSession({ sessionId }).catch(() => {});
    };
     
  }, []);
}
